import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Chip , Card, CardContent } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import api from '../services/api';
import { toast } from 'react-toastify';

function InfluencerFinalCostStep({ campaignId, user }) {
  const [influencers, setInfluencers] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  useEffect(() => {
    if (campaignId) fetchApprovedInfluencers();
  }, [campaignId]);

  const fetchApprovedInfluencers = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/campaign/${campaignId}`);
      const campaignData = response.data?.data;
      setCampaign(campaignData);
      if (!campaignData) return;
      const campaign = campaignData;

      const approvedIds = (campaign.influencerList || [])
        .filter(item => item.influencerApprovalId)
        .map(item => item.influencerApprovalId.toString());

      if (!approvedIds.length) {
        setInfluencers([]);
        return;
      }

      const infResponse = await api.get('/influencers');
      const allInfluencers = infResponse.data || [];

      const negotiationMap = {};
      (campaign.influencerNegotiation || []).forEach(neg => {
        negotiationMap[neg.influencerId?.toString()] = neg.negotiationCost || 0;
      });

      const approved = allInfluencers
        .filter(inf => approvedIds.includes(inf._id))
        .map(inf => ({
          ...inf,
          finalCost: negotiationMap[inf._id] || inf.reelCost || 0,
        }));

      setInfluencers(approved);
    } catch (error) {
      console.error('Failed to fetch approved influencers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedRows.length) return;
    try {
      setSelectedRows([]);
      fetchApprovedInfluencers();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit final costs');
    }
  };

  const handleCancel = () => {
    setSelectedRows([]);
    fetchApprovedInfluencers();
  };

  const getChipColor = (type) => {
    switch (type) {
      case 'Micro': return 'primary';
      case 'Macro': return 'secondary';
      case 'Mid-tier': return 'success';
      case 'Mega': return 'warning';
      case 'Celeb': return 'error';
      default: return 'default';
    }
  };

  const columns = [
    { field: 'sno', headerName: 'S.No', width: 70 },
    { field: 'name', headerName: 'Name', width: 150 },
    { field: 'onboarder', headerName: 'Onboarder', width: 130 },
    { field: 'state', headerName: 'State', width: 120 },
    { field: 'city', headerName: 'City', width: 120 },
    { field: 'category', headerName: 'Category', width: 130 },
    { field: 'contactNumber', headerName: 'Number', width: 130 },
    {
      field: 'instagramInfluencerType', headerName: 'Instagram Type', width: 180,
      renderCell: (params) => {
        const type = params.row.allData?.instagramInfluencerType;
        if (!type || type === '-') return '-';
        return <Chip label={type} color={getChipColor(type)} size="small" />;
      }
    },
    {
      field: 'totalCostInstagram', headerName: 'Total Cost (Instagram)', width: 180, cellClassName: 'instagram-cell',
      renderCell: (params) => {
        const total = (params.row.allData?.reelCost || 0) + (params.row.allData?.storyCost || 0);
        return total > 0 ? `₹${total.toLocaleString()}` : '-';
      }
    },
    {
      field: 'negotiationCostInstagram', headerName: 'Negotiation Total Cost (Instagram)', width: 220, cellClassName: 'instagram-cell',
      renderCell: (params) => {
        const cost = params.row.allData?.negotiationCost?.reelCost || 0;
        return cost > 0 ? `₹${cost.toLocaleString()}` : '-';
      }
    },
  ];

  const rows = influencers.map((inf, index) => ({
    id: inf._id,
    sno: index + 1,
    name: inf.name || '-',
    onboarder: inf.onboarderId?.name || 'N/A',
    state: inf.state || '-',
    city: inf.city || '-',
    category: inf.category || '-',
    contactNumber: inf.contactNumber || '-',
    allData: inf,
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Influencer Final Cost
          </Typography>

          <Box sx={{ display: 'flex', gap: 4, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              Expected Influencer: <strong style={{ color: '#1565c0' }}>{campaign?.dmTeam?.creators?.reduce((sum, c) => sum + (parseInt(c.numberOfCreators) || 0), 0) || 0}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Allocated Budget: <strong style={{ color: '#2e7d32' }}>₹{(campaign?.brandTeam?.budget || 0).toLocaleString()}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Actual Budget: <strong style={{ color: '#e65100' }}>₹{influencers.reduce((sum, inf) => sum + (inf.reelCost || 0) + (inf.storyCost || 0), 0).toLocaleString()}</strong>
            </Typography>
          </Box>
  
          <DataGrid
            columns={columns}
            rows={loading ? [] : rows}
            loading={loading}
            checkboxSelection
            onSelectionModelChange={(ids) => setSelectedRows(ids)}
            rowSelectionModel={selectedRows}
            pageSize={10}
            autoHeight
            sx={{
              boxShadow: 2,
              backgroundColor: 'white',
              '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8f9fa' },
              '& .MuiDataGrid-iconSeparator': { display: 'none' },
              '& .MuiDataGrid-row': {
                backgroundColor: 'white',
                borderLeft: 'solid 1px #e5e7eb',
                borderRight: 'solid 1px #e5e7eb',
              },
            }}
          />
          {selectedRows.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                // onClick={handleCancel}
                sx={{ color: '#666', borderColor: '#ddd' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                // onClick={handleSubmit}
              >
                Submit ({selectedRows.length})
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

    </Box>
  );
}

export default InfluencerFinalCostStep;
